import { useQuery } from '@tanstack/react-query';

import { api, type Collection } from '@/lib/api';
import { matchesMultiTokenTextSearch } from '@/lib/text-search';
import { formatDate, formatMoney } from '@/utils/format';

export type GlobalSearchScope =
  | 'members'
  | 'departments'
  | 'groups'
  | 'services'
  | 'programs'
  | 'funds'
  | 'contributions'
  | 'pledges'
  | 'projects'
  | 'expenses'
  | 'registers'
  | 'assets'
  | 'documents'
  | 'visits';

export type GlobalSearchAccess = Record<GlobalSearchScope, boolean>;

export interface GlobalSearchResultItem {
  id: string;
  title: string;
  subtitle?: string;
  detail?: string;
  href: string;
}

export interface GlobalSearchResultSection {
  scope: GlobalSearchScope;
  total: number;
  items: GlobalSearchResultItem[];
  hasError: boolean;
}

interface UseGlobalSearchParams {
  query: string;
  limit?: number;
  access: GlobalSearchAccess;
}

interface MemberHit {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  email: string | null;
  phone: string | null;
  memberType: string;
  status: string;
}

interface DepartmentHit {
  id: string;
  name: string;
  description: string | null;
}

interface GroupHit {
  id: string;
  name: string;
  type: string;
  memberCount: number;
}

interface GatheringHit {
  id: string;
  name: string;
  kind: 'service' | 'program';
}

interface FundHit {
  id: string;
  name: string;
  code: string;
  fundType: string;
  isActive: boolean;
}

interface ContributionHit {
  id: string;
  giverName: string | null;
  identityHidden: boolean;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  receiptNumber: number;
  isVoided: boolean;
}

interface PledgeHit {
  id: string;
  memberName: string;
  totalAmountMinor: number;
  currency: string;
  status: string;
}

interface ProjectHit {
  id: string;
  name: string;
  status: string;
  progressPercent: number;
  targetAmountMinor: number;
  currency: string;
}

interface ExpenseHit {
  id: string;
  payee: string;
  amountMinor: number;
  currency: string;
  expenseDate: string;
  status: string;
}

type RegisterKind = 'baptisms' | 'confirmations' | 'marriages' | 'funerals' | 'transfers';

interface RegisterHit {
  id: string;
  kind: RegisterKind;
  personName?: string;
  spouseOneName?: string;
  spouseTwoName?: string;
  baptismDate?: string;
  confirmationDate?: string;
  marriageDate?: string;
  serviceDate?: string;
  dateOfDeath?: string | null;
  transferDate?: string;
  ministerName?: string | null;
  officiatingMinisters?: string[];
  spouseOneWitnesses?: Array<{ name?: string | null }>;
  spouseTwoWitnesses?: Array<{ name?: string | null }>;
  officiantName?: string | null;
  place?: string | null;
  cemetery?: string | null;
  baptismNumber?: string | null;
  confirmationNumber?: string | null;
  certificateNumber?: string | null;
  letterReference?: string | null;
  otherChurchName?: string | null;
}

interface AssetHit {
  id: string;
  name: string;
  kind: string;
  assetTag: string | null;
  serialNumber: string | null;
  status: string;
}

interface DocumentHit {
  id: string;
  title: string;
  categoryId: string;
  categoryName: string;
  fileName: string;
  status: string;
}

interface VisitHit {
  id: string;
  visitDate: string;
  kind: string;
  summary: string | null;
}

const REGISTER_KINDS: readonly RegisterKind[] = [
  'baptisms',
  'confirmations',
  'marriages',
  'funerals',
  'transfers',
];

const registerTitle = (kind: RegisterKind, row: RegisterHit): string => {
  if (kind === 'marriages') {
    return [row.spouseOneName, row.spouseTwoName].filter(Boolean).join(' & ');
  }
  return row.personName ?? '';
};

const registerDate = (kind: RegisterKind, row: RegisterHit): string | undefined => {
  switch (kind) {
    case 'baptisms':
      return row.baptismDate;
    case 'confirmations':
      return row.confirmationDate;
    case 'marriages':
      return row.marriageDate;
    case 'funerals':
      return row.dateOfDeath ?? undefined;
    case 'transfers':
      return row.transferDate;
    default:
      return undefined;
  }
};

const registerMatchFields = (kind: RegisterKind, row: RegisterHit): Array<string | null | undefined> => {
  switch (kind) {
    case 'marriages':
      return [
        row.spouseOneName,
        row.spouseTwoName,
        ...(row.spouseOneWitnesses ?? []).map((witness) => witness.name),
        ...(row.spouseTwoWitnesses ?? []).map((witness) => witness.name),
        ...(row.officiatingMinisters ?? []),
        row.ministerName,
        row.place,
        row.certificateNumber,
      ];
    case 'transfers':
      return [row.personName, row.otherChurchName, row.letterReference];
    case 'funerals':
      return [row.personName, row.officiantName, row.place, row.cemetery];
    case 'confirmations':
      return [row.personName, row.ministerName, row.place, row.confirmationNumber];
    case 'baptisms':
    default:
      return [row.personName, row.ministerName, row.place, row.baptismNumber];
  }
};

const firstDefined = (...values: Array<string | null | undefined>): string | undefined =>
  values.find((value): value is string => typeof value === 'string' && value.trim().length > 0);

const personName = (row: Pick<MemberHit, 'firstName' | 'middleName' | 'lastName'>): string =>
  [row.firstName, row.middleName, row.lastName].filter(Boolean).join(' ');

async function loadSearchSection(
  enabled: boolean,
  scope: GlobalSearchScope,
  load: () => Promise<{ total: number; items: GlobalSearchResultItem[] }>,
): Promise<GlobalSearchResultSection | null> {
  if (!enabled) return null;

  try {
    const section = await load();
    return {
      scope,
      total: section.total,
      items: section.items,
      hasError: false,
    };
  } catch {
    return {
      scope,
      total: 0,
      items: [],
      hasError: true,
    };
  }
}

/**
 * Fan-out global search across module list endpoints the caller may access.
 *
 * Access is gated client-side so we never call endpoints the user cannot see;
 * each endpoint still enforces its own permission middleware and privacy rules
 * (e.g. giver identity on contributions).
 */
export function useGlobalSearch({ query, limit = 5, access }: UseGlobalSearchParams) {
  const normalizedQuery = query.trim().toLowerCase();
  const scopeSignature = Object.entries(access)
    .filter(([, enabled]) => enabled)
    .map(([scope]) => scope)
    .join(',');

  return useQuery({
    queryKey: ['globalSearch', normalizedQuery, limit, scopeSignature],
    enabled: normalizedQuery.length >= 2,
    queryFn: async () => {
      const clientFilterLimit = Math.max(limit * 4, 40);

      const sections = await Promise.all([
        loadSearchSection(access.members, 'members', async () => {
          const result = await api.get<Collection<MemberHit>>('/people/members', {
            query: { page: 1, pageSize: limit, q: normalizedQuery },
          });
          return {
            total: result.page.totalItems,
            items: result.data.map((member) => ({
              id: member.id,
              title: personName(member),
              subtitle: firstDefined(member.phone, member.email, member.memberType),
              href: `/people/${member.id}`,
            })),
          };
        }),

        loadSearchSection(access.departments, 'departments', async () => {
          const result = await api.get<Collection<DepartmentHit>>('/people/departments', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((department) =>
            matchesMultiTokenTextSearch(normalizedQuery, [
              department.name,
              department.description,
            ]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((department) => ({
              id: department.id,
              title: department.name,
              subtitle: department.description ?? undefined,
              href: `/people/departments/${department.id}`,
            })),
          };
        }),

        loadSearchSection(access.groups, 'groups', async () => {
          const result = await api.get<Collection<GroupHit>>('/groups', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((group) =>
            matchesMultiTokenTextSearch(normalizedQuery, [group.name, group.type]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((group) => ({
              id: group.id,
              title: group.name,
              subtitle: group.type,
              href: `/groups/${group.id}`,
            })),
          };
        }),

        loadSearchSection(access.services, 'services', async () => {
          const result = await api.get<Collection<GatheringHit>>('/programs', {
            query: { kind: 'service', page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((row) =>
            matchesMultiTokenTextSearch(normalizedQuery, [row.name]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((row) => ({
              id: row.id,
              title: row.name,
              href: `/services/${row.id}`,
            })),
          };
        }),

        loadSearchSection(access.programs, 'programs', async () => {
          const result = await api.get<Collection<GatheringHit>>('/programs', {
            query: { kind: 'program', page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((row) =>
            matchesMultiTokenTextSearch(normalizedQuery, [row.name]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((row) => ({
              id: row.id,
              title: row.name,
              href: `/programs/${row.id}`,
            })),
          };
        }),

        loadSearchSection(access.funds, 'funds', async () => {
          const result = await api.get<Collection<FundHit>>('/giving/funds', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((fund) =>
            matchesMultiTokenTextSearch(normalizedQuery, [fund.name, fund.code, fund.fundType]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((fund) => ({
              id: fund.id,
              title: fund.name,
              subtitle: firstDefined(fund.code, fund.fundType),
              href: `/giving/funds/${fund.id}`,
            })),
          };
        }),

        loadSearchSection(access.contributions, 'contributions', async () => {
          const result = await api.get<Collection<ContributionHit>>('/giving/contributions', {
            query: { page: 1, pageSize: limit, q: normalizedQuery },
          });
          return {
            total: result.page.totalItems,
            items: result.data.map((row) => ({
              id: row.id,
              title: formatMoney(row.amountMinor, row.currency),
              subtitle: firstDefined(
                row.identityHidden ? undefined : row.giverName ?? undefined,
                formatDate(row.contributionDate),
              ),
              detail: `#${row.receiptNumber}`,
              href: '/giving',
            })),
          };
        }),

        loadSearchSection(access.pledges, 'pledges', async () => {
          const result = await api.get<Collection<PledgeHit>>('/giving/pledges', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((pledge) =>
            matchesMultiTokenTextSearch(normalizedQuery, [pledge.memberName, pledge.status]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((pledge) => ({
              id: pledge.id,
              title: pledge.memberName,
              subtitle: formatMoney(pledge.totalAmountMinor, pledge.currency),
              detail: pledge.status,
              href: '/projects',
            })),
          };
        }),

        loadSearchSection(access.projects, 'projects', async () => {
          const result = await api.get<Collection<ProjectHit>>('/projects', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((project) =>
            matchesMultiTokenTextSearch(normalizedQuery, [project.name, project.status]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((project) => ({
              id: project.id,
              title: project.name,
              subtitle: formatMoney(project.targetAmountMinor, project.currency),
              detail: project.status,
              href: '/projects',
            })),
          };
        }),

        loadSearchSection(access.expenses, 'expenses', async () => {
          const result = await api.get<Collection<ExpenseHit>>('/expenses', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((expense) =>
            matchesMultiTokenTextSearch(normalizedQuery, [expense.payee, expense.status]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((expense) => ({
              id: expense.id,
              title: expense.payee,
              subtitle: formatMoney(expense.amountMinor, expense.currency),
              detail: formatDate(expense.expenseDate),
              href: '/expenses',
            })),
          };
        }),

        loadSearchSection(access.registers, 'registers', async () => {
          const sections = await Promise.all(
            REGISTER_KINDS.map(async (kind) => {
              const result = await api.get<Collection<RegisterHit>>(`/sacraments/${kind}`, {
                query: { page: 1, pageSize: clientFilterLimit },
              });
              const matched = result.data
                .map((row) => ({ ...row, kind }))
                .filter((row) =>
                  matchesMultiTokenTextSearch(normalizedQuery, registerMatchFields(kind, row)),
                );
              return matched;
            }),
          );

          const matched = sections.flat();
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((row) => ({
              id: `${row.kind}-${row.id}`,
              title: registerTitle(row.kind, row),
              subtitle: registerDate(row.kind, row)
                ? formatDate(registerDate(row.kind, row)!)
                : undefined,
              detail: firstDefined(
                row.officiatingMinisters?.map((name) => name.trim()).filter(Boolean).join(', '),
                row.ministerName,
                row.officiantName,
                row.place,
              ),
              href: `/sacraments/${row.kind}`,
            })),
          };
        }),

        loadSearchSection(access.assets, 'assets', async () => {
          const result = await api.get<Collection<AssetHit>>('/assets', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((asset) =>
            matchesMultiTokenTextSearch(normalizedQuery, [
              asset.name,
              asset.kind,
              asset.assetTag,
              asset.serialNumber,
              asset.status,
            ]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((asset) => ({
              id: asset.id,
              title: asset.name,
              subtitle: firstDefined(asset.assetTag, asset.serialNumber, asset.kind),
              href: '/assets',
            })),
          };
        }),

        loadSearchSection(access.documents, 'documents', async () => {
          const result = await api.get<Collection<DocumentHit>>('/documents', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((document) =>
            matchesMultiTokenTextSearch(normalizedQuery, [
              document.title,
              document.categoryName,
              document.fileName,
              document.status,
            ]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((document) => ({
              id: document.id,
              title: document.title,
              subtitle: document.categoryName,
              detail: document.fileName,
              href: `/documents?category=${document.categoryId}`,
            })),
          };
        }),

        loadSearchSection(access.visits, 'visits', async () => {
          const result = await api.get<Collection<VisitHit>>('/pastoral/visits', {
            query: { page: 1, pageSize: clientFilterLimit },
          });
          const matched = result.data.filter((visit) =>
            matchesMultiTokenTextSearch(normalizedQuery, [visit.summary, visit.kind]),
          );
          return {
            total: matched.length,
            items: matched.slice(0, limit).map((visit) => ({
              id: visit.id,
              title: visit.summary?.trim() || visit.kind,
              subtitle: formatDate(visit.visitDate),
              detail: visit.kind,
              href: '/pastoral/visits',
            })),
          };
        }),
      ]);

      return sections.filter((section): section is GlobalSearchResultSection => section !== null);
    },
  });
}
